// The framework-agnostic core of the formatting funnel (contract 04 §2, §9, §13.3).
//
// Holds the three shared primitives every pure formatter reuses:
//   • `guard`      — the single null/NaN/non-finite → `'-'` sentinel check (contract §5, §14).
//   • `safeLocale` — accepts ONLY a BCP-47 string; a non-string resolves to the fallback locale
//                    instead of silently dropping to the runtime default (the root cause of the
//                    dead workspace-language signal — analysis §2.3).
//   • `getNumberFormat` — the memoised `Intl.NumberFormat` cache keyed by `locale|options`, with a
//                    `RangeError → en-US` fallback that is cached so a bad locale fails once
//                    (contract §14; legacy `_safeLocale`/`_dtfCache`, `format.js:497–541`).
//
// Zero Vue, zero Pinia, zero network — pure and unit-testable in isolation (contract §4).
//
// (`createFormatter` and the per-domain modules form an intentional import cycle: the pure modules
// import these primitives, and the factory delegates back to them. It is safe because every use is
// deferred to call time, never module-evaluation time.)

import * as cur from './currency'
import * as dt from './date'
import * as dur from './duration'
import * as fs from './filesize'
import * as list from './list'
import * as num from './number'
import * as pct from './percent'
import * as rel from './relative'
import type {
  DateInput,
  FormatCurrencyOptions,
  FormatDateOptions,
  FormatDateRangeOptions,
  FormatDurationOptions,
  FormatFileSizeOptions,
  FormatListOptions,
  FormatNumberOptions,
  FormatPercentOptions,
  FormatRelativeOptions,
} from './types'

/** The uniform empty rendering for nullish / NaN / non-finite input (legacy parity, contract §5). */
export const SENTINEL = '-'

/** BCP-47 locale used whenever a requested locale is unusable (contract §14). */
export const FALLBACK_LOCALE = 'en-US'

/**
 * True when `value` must render as the `'-'` sentinel: `null`, `undefined`, `NaN`, or ±Infinity.
 * A finite number passes. This is the ONE guard every formatter shares so they behave identically.
 */
export function guard(value: number | null | undefined): value is null | undefined {
  return value == null || typeof value !== 'number' || !Number.isFinite(value)
}

/**
 * Normalise a locale for Intl. Accepts only a non-empty BCP-47 string; anything else (an object,
 * an empty string, `undefined`) resolves to {@link FALLBACK_LOCALE} rather than the runtime default.
 * Invalid-but-string tags are left for the Intl constructor to reject in {@link getNumberFormat}.
 */
export function safeLocale(locale: string): string {
  return typeof locale === 'string' && locale.trim() !== '' ? locale : FALLBACK_LOCALE
}

/** Intl instances are expensive; cap the cache so a hostile key stream cannot grow it unbounded. */
const INTL_FORMAT_CACHE_LIMIT = 256
const numberFormatCache = new Map<string, Intl.NumberFormat>()

/** Deterministic key for a flat `Intl.NumberFormatOptions` bag (sorted, `undefined` values dropped). */
function optionsKey(options: Intl.NumberFormatOptions): string {
  const record = options as Record<string, unknown>
  let key = ''
  for (const name of Object.keys(record).sort()) {
    const value = record[name]
    if (value !== undefined) key += `${name}=${String(value)};`
  }
  return key
}

function remember(key: string, format: Intl.NumberFormat): Intl.NumberFormat {
  numberFormatCache.set(key, format)
  if (numberFormatCache.size > INTL_FORMAT_CACHE_LIMIT) {
    const oldest = numberFormatCache.keys().next().value
    if (oldest !== undefined) numberFormatCache.delete(oldest)
  }
  return format
}

/**
 * Memoised `Intl.NumberFormat` getter. The same `locale|options` returns the same instance. A
 * `RangeError` (bad locale or option) falls back to {@link FALLBACK_LOCALE}; if the options
 * themselves are invalid, they are dropped as a last resort so a formatter never throws.
 */
export function getNumberFormat(
  locale: string,
  options: Intl.NumberFormatOptions = {},
): Intl.NumberFormat {
  const tag = safeLocale(locale)
  const key = `${tag}|${optionsKey(options)}`
  const cached = numberFormatCache.get(key)
  if (cached) return cached

  try {
    return remember(key, new Intl.NumberFormat(tag, options))
  } catch {
    try {
      return remember(key, new Intl.NumberFormat(FALLBACK_LOCALE, options))
    } catch {
      return remember(key, new Intl.NumberFormat(FALLBACK_LOCALE))
    }
  }
}

/** Decimal and grouping glyphs resolved from one locale-aware Intl formatter. */
export interface NumberSeparators {
  decimal: string
  group: string
}

/**
 * Resolve the locale's decimal and grouping glyphs without duplicating Intl construction in
 * presentation adapters. `Highcharts` consumes this through module 02 so its global axis and
 * tooltip formatting follows the same locale source as every other number surface.
 */
export function resolveNumberSeparators(locale: string): NumberSeparators {
  const parts = getNumberFormat(locale, {
    useGrouping: true,
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).formatToParts(1000.1)

  return {
    decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.',
    group: parts.find((part) => part.type === 'group')?.value ?? ',',
  }
}

/** Browser/runner IANA zone used by vendor adapters that need the legacy local-time behavior. */
export function resolveLocalTimeZone(): string {
  try {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/** Clamp a fraction-digit count to the `[0, 20]` range Intl accepts (contract §14, legacy cap). */
export function clampFraction(digits: number): number {
  return Math.min(Math.max(Math.trunc(digits), 0), 20)
}

/**
 * Resolve `minimumFractionDigits` / `maximumFractionDigits` from caller options. When neither is
 * given, `fallback` supplies fixed defaults (heavy-number rule / percent's 2 digits); currencies
 * pass no fallback so Intl uses each currency's natural precision.
 */
export function resolveFraction(
  options: Pick<FormatNumberOptions, 'minFrac' | 'maxFrac'>,
  fallback?: { min: number; max: number },
): Intl.NumberFormatOptions {
  const min = options.minFrac === undefined ? undefined : clampFraction(options.minFrac)
  const max = options.maxFrac === undefined ? undefined : clampFraction(options.maxFrac)

  if (min === undefined && max === undefined) {
    return fallback
      ? { minimumFractionDigits: fallback.min, maximumFractionDigits: fallback.max }
      : {}
  }

  const out: Intl.NumberFormatOptions = {}
  if (min !== undefined) out.minimumFractionDigits = min
  if (max !== undefined) out.maximumFractionDigits = min !== undefined && max < min ? min : max
  return out
}

/** Build the grouping / rounding / sign fields shared by every numeric formatter. */
export function commonNumberOptions(options: FormatNumberOptions): Intl.NumberFormatOptions {
  const out: Intl.NumberFormatOptions = { useGrouping: options.grouping ?? true }
  if (options.round === 'trunc') out.roundingMode = 'trunc'
  if (options.signDisplay !== undefined) out.signDisplay = options.signDisplay
  return out
}

/** Default currency + optional config baked into a formatter instance (contract §9). */
export interface CreateFormatterConfig {
  /** ISO-4217 code used by `formatCurrency` when a call passes no explicit `currency`. */
  currency?: string
}

/** The numeric + duration subset of the reactive `Formatter` (contract §5); the remaining
 *  ORP-18594 domains (date/relative/filesize/list) join here as their consumers land. */
export interface NumberFormatter {
  /** The active Intl locale this formatter resolves to. */
  readonly locale: string
  formatNumber(value: number | null | undefined, options?: FormatNumberOptions): string
  formatInteger(value: number | null | undefined): string
  formatCurrency(value: number | null | undefined, options?: FormatCurrencyOptions): string
  formatPercent(value: number | null | undefined, options?: FormatPercentOptions): string
  formatCompact(value: number | null | undefined, options?: { maxFrac?: number }): string
  formatDuration(value: number | null | undefined, options?: FormatDurationOptions): string
  formatDate(value: string | number | Date | null | undefined, options?: FormatDateOptions): string
  formatDateTime(
    value: string | number | Date | null | undefined,
    options?: FormatDateOptions,
  ): string
  formatTime(value: string | number | Date | null | undefined, options?: FormatDateOptions): string
  formatDateRange(start: DateInput, end: DateInput, options?: FormatDateRangeOptions): string
}

/** Complete value-formatting surface exposed by `useFormat()` (contract 04 §5/§9). */
export interface Formatter extends NumberFormatter {
  formatDate(value: DateInput, options?: FormatDateOptions): string
  formatDateTime(value: DateInput, options?: FormatDateOptions): string
  formatTime(value: DateInput, options?: FormatDateOptions): string
  formatRelative(value: DateInput, options?: FormatRelativeOptions): string
  formatFileSize(value: number | null | undefined, options?: FormatFileSizeOptions): string
  formatList(values: readonly string[] | null | undefined, options?: FormatListOptions): string
}

/**
 * Build a locale-bound numeric formatter over the pure per-domain modules. `locale` may be a string
 * or a getter (so `useFormat` can pass `() => localeStore.intlLocale` and stay reactive) — the
 * factory itself stays framework-agnostic. `config.currency` is the default currency; a per-call
 * `options.currency` overrides it.
 */
export function createFormatter(
  locale: string | (() => string),
  config: CreateFormatterConfig = {},
): Formatter {
  const resolveLocale = typeof locale === 'function' ? locale : () => locale
  const defaultCurrency = config.currency

  const withCurrency = (options: FormatCurrencyOptions = {}): FormatCurrencyOptions =>
    defaultCurrency !== undefined && options.currency === undefined
      ? { ...options, currency: defaultCurrency }
      : options

  return {
    get locale(): string {
      return safeLocale(resolveLocale())
    },
    formatNumber: (value, options) => num.formatNumber(value, resolveLocale(), options),
    formatInteger: (value) => num.formatInteger(value, resolveLocale()),
    formatCompact: (value, options) => num.formatCompact(value, resolveLocale(), options),
    formatPercent: (value, options) => pct.formatPercent(value, resolveLocale(), options),
    formatCurrency: (value, options) =>
      cur.formatCurrency(value, resolveLocale(), withCurrency(options)),
    formatDuration: (value, options) => dur.formatDuration(value, resolveLocale(), options),
    formatDate: (value, options) => dt.formatDate(value, resolveLocale(), options),
    formatDateTime: (value, options) => dt.formatDateTime(value, resolveLocale(), options),
    formatTime: (value, options) => dt.formatTime(value, resolveLocale(), options),
    formatDateRange: (start, end, options) =>
      dt.formatDateRange(start, end, resolveLocale(), options),
    formatRelative: (value, options) => rel.formatRelative(value, resolveLocale(), options),
    formatFileSize: (value, options) => fs.formatFileSize(value, resolveLocale(), options),
    formatList: (values, options) => list.formatList(values, resolveLocale(), options),
  }
}
